import clsx from 'clsx';
import { useState } from 'react';
import { usePDPData, usePDPState } from '~/features/product-details/context';
import { SalesforcePromotionCountdown } from '~/lib/salesforce/types';
import { Typography } from '~/shared/components';
import { Countdown } from '~/shared/components/Countdown';
import { useUtils } from '~/shared/hooks/useUtils';
import { toOre } from '~/shared/utils/numbers';
import { useTranslation } from '~/shared/utils/translation';
import styles from './ProductPromotionCountdown.module.scss';

type Props = Omit<
    React.ComponentProps<typeof Countdown>,
    'endDate' | 'headline' | 'expiredText' | 'onExpire'
>;

/** What one unit saves once the promotion starts; null when the promotion has no unit price. */
const getSavings = ({ price, discountedPrice }: SalesforcePromotionCountdown): number | null => {
    if (price === null || discountedPrice === null) return null;

    const savings = (toOre(price) - toOre(discountedPrice)) / 100;

    return savings > 0 ? savings : null;
};

/**
 * Counts down to the moment Salesforce starts applying a promotion's discount in the basket. Once
 * it has, only a line saying the offer is active remains, for as long as the visitor stays.
 */
export const ProductPromotionCountdown: React.FC<Props> = ({ className, ...rest }) => {
    const { translate } = useTranslation();
    const { convertPriceToLocale } = useUtils();
    const { product } = usePDPData();
    const { promotionCountdown } = usePDPState();

    // At zero the countdown hook stops returning the promotion, so the last one seen is kept to
    // say it is active. Tied to the product, so another variant starts clean.
    const [lastSeen, setLastSeen] = useState<{
        productId: string;
        promotion: SalesforcePromotionCountdown;
    }>();

    if (promotionCountdown && lastSeen?.promotion !== promotionCountdown) {
        setLastSeen({ productId: product.id, promotion: promotionCountdown });
    }

    const promotion =
        promotionCountdown ?? (lastSeen?.productId === product.id ? lastSeen.promotion : undefined);
    const endDate = promotion?.countdown.countdownEndDate;

    if (!promotion || !endDate) return null;

    const savings = getSavings(promotion);
    const amount =
        savings === null
            ? null
            : convertPriceToLocale(savings, { wholeNumber: Number.isInteger(savings) });

    const countdownHeadline = amount
        ? translate('pdp.countdown.promotion.headline', { amount }) || `Spar ${amount} kr. om`
        : translate('pdp.countdown.promotion.headlineWithoutAmount') || 'Tilbuddet starter om';
    const headline = promotionCountdown
        ? countdownHeadline
        : translate('pdp.countdown.promotion.offerIsLive') || 'Tilbuddet er nu aktivt!';

    return (
        <div className={clsx(styles.productPromotionCountdown, className)}>
            <div className={styles.headline}>
                <Typography variant="paragraph-XL" weight="medium" as="span">
                    {headline}
                </Typography>
            </div>

            {/* Once it has started there is nothing left to count down: only the headline stays. */}
            {promotionCountdown && <Countdown {...rest} endDate={endDate} />}
        </div>
    );
};
